import React, { useEffect, useRef, useState } from "react";
import { Send, ArrowLeft } from "lucide-react";

import { useAuth } from "@/components/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";

import { ChatContext } from "./ChatContext";
import { ChatSelector } from "./ChatSelector";

function normalizeProduct(product) {
  return product
    ? {
        code: product.code,
        description: product.description ?? null,
      }
    : null;
}

function normalizeCustomer(customer) {
  return customer
    ? {
        code: customer.code,
        name: customer.name ?? null,
      }
    : null;
}

function normalizeRepresentative(representative) {
  return representative
    ? {
        cod_rep: representative.cod_rep,
        name: representative.name ?? null,
      }
    : null;
}

function resolveChatError(error) {
  const status = error?.context?.status;
  const code = error?.context?.response?.error?.code;

  if (status === 401 || code === "UNAUTHENTICATED") {
    return "Por favor, inicia sesión para usar el chat.";
  }

  if (status === 403 || code === "FORBIDDEN") {
    return "No tienes permisos para usar el chat IA o el servicio no está habilitado.";
  }

  if (status === 422 || code === "INVALID_REQUEST") {
    return "La petición del chat no es válida.";
  }

  return "Lo siento, hubo un error al procesar tu mensaje.";
}

export function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [context, setContext] = useState({
    selectedProduct: null,
    selectedCustomer: null,
    selectedRepresentative: null,
  });

  const messagesEndRef = useRef(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!user?.id) {
      setMessages([]);
      setConversationId(null);
      setSessionId(null);
      return;
    }

    loadLatestConversation();
  }, [user?.id]);

  const loadLatestConversation = async () => {
    setHistoryLoading(true);

    try {
      const { data: latestConversation, error: conversationError } = await supabase
        .from("chat_conversations")
        .select("id, session_id, last_context")
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (conversationError) {
        throw conversationError;
      }

      if (!latestConversation) {
        setMessages([]);
        setConversationId(null);
        setSessionId(null);
        return;
      }

      setConversationId(latestConversation.id);
      setSessionId(latestConversation.session_id);
      setContext({
        selectedProduct: latestConversation.last_context?.product ?? null,
        selectedCustomer: latestConversation.last_context?.customer ?? null,
        selectedRepresentative: latestConversation.last_context?.representative ?? null,
      });

      const { data: storedMessages, error: messagesError } = await supabase
        .from("chat_messages")
        .select("role, content")
        .eq("conversation_id", latestConversation.id)
        .order("created_at", { ascending: true });

      if (messagesError) {
        throw messagesError;
      }

      setMessages((storedMessages || []).map((message) => ({
        role: message.role,
        content: message.content,
      })));
    } catch (error) {
      console.error("Error loading chat history:", error);
      toast({
        title: "Error en historial",
        description: "No se pudo cargar el historial del chat.",
        variant: "destructive",
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleNewConversation = () => {
    setConversationId(null);
    setSessionId(null);
    setMessages([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const message = input.trim();
    const optimisticUserMessage = { role: "user", content: message };

    setLoading(true);
    setInput("");
    setMessages((prev) => [...prev, optimisticUserMessage]);

    try {
      if (!user) {
        throw new Error("Usuario no autenticado");
      }

      const payload = {
        conversationId,
        sessionId,
        message,
        context: {
          product: normalizeProduct(context.selectedProduct),
          customer: normalizeCustomer(context.selectedCustomer),
          representative: normalizeRepresentative(context.selectedRepresentative),
        },
      };

      const { data, error } = await supabase.functions.invoke("ai-chat", {
        body: payload,
      });

      if (error) {
        throw error;
      }

      setConversationId(data?.conversationId ?? null);
      setSessionId(data?.sessionId ?? null);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data?.response ?? "No se recibió respuesta del asistente.",
        },
      ]);
    } catch (error) {
      console.error("Error sending message to ai-chat:", error);
      const errorMessage =
        error?.message === "Usuario no autenticado"
          ? "Por favor, inicia sesión para usar el chat."
          : resolveChatError(error);

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: errorMessage,
        },
      ]);

      toast({
        title: "Error en chat IA",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product) => {
    setContext((prev) => ({ ...prev, selectedProduct: product }));
  };

  const handleSelectCustomer = (customer) => {
    setContext((prev) => ({ ...prev, selectedCustomer: customer }));
  };

  const handleSelectRepresentative = (representative) => {
    setContext((prev) => ({ ...prev, selectedRepresentative: representative }));
  };

  const handleClearContext = () => {
    setContext({
      selectedProduct: null,
      selectedCustomer: null,
      selectedRepresentative: null,
    });
  };

  const handleClearProduct = () => {
    setContext((prev) => ({ ...prev, selectedProduct: null }));
  };

  const handleClearCustomer = () => {
    setContext((prev) => ({ ...prev, selectedCustomer: null }));
  };

  const handleClearRepresentative = () => {
    setContext((prev) => ({ ...prev, selectedRepresentative: null }));
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={() => (window.location.hash = "#/products")}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <Button variant="outline" onClick={handleNewConversation} disabled={loading || historyLoading}>
            Nueva conversación
          </Button>
        </div>
      </div>

      <div className="p-4 border-b bg-gray-50">
        <div className="flex flex-wrap gap-2">
          <ChatSelector
            type="product"
            onSelect={handleSelectProduct}
            placeholder="Producto"
          />
          <ChatSelector
            type="customer"
            onSelect={handleSelectCustomer}
            placeholder="Cliente"
          />
          <ChatSelector
            type="representative"
            onSelect={handleSelectRepresentative}
            placeholder="Representante"
          />
        </div>
      </div>

      <ChatContext
        selectedProduct={context.selectedProduct}
        selectedCustomer={context.selectedCustomer}
        selectedRepresentative={context.selectedRepresentative}
        onClearContext={handleClearContext}
        onClearProduct={handleClearProduct}
        onClearCustomer={handleClearCustomer}
        onClearRepresentative={handleClearRepresentative}
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {historyLoading && (
          <div className="text-sm text-gray-500">Cargando historial...</div>
        )}

        {!historyLoading && messages.length === 0 && (
          <div className="text-sm text-gray-500">
            No hay mensajes todavía. Escribe tu primera consulta.
          </div>
        )}

        {messages.map((message, index) => (
          <div
            key={`${message.role}-${index}`}
            className={`flex ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                message.role === "user"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-900"
              }`}
            >
              {message.content}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={user ? "Escribe tu mensaje..." : "Inicia sesión para usar el chat"}
            disabled={loading || !user}
          />
          <Button type="submit" disabled={loading || !user}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
